import React from "react";

/**
 * Animated blueprint-style cityscape.
 * Buildings and a house are drawn from lines (stroke-dashoffset animation),
 * windows pulse, and the whole skyline gently floats.
 * All strokes use the mint accent (#38E5B6).
 */
export function AnimatedBuildingBackground() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden -z-10"
      style={{ color: "var(--primary)" }}
    >
      {/* Radial glow behind the skyline */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 120%, rgba(56,229,182,0.18), transparent 60%)",
        }}
      />

      {/* Faint drifting grid (the "blueprint paper") */}
      <svg
        className="absolute inset-0 h-full w-full"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <pattern
            id="grid"
            width="40"
            height="40"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M40 0H0V40"
              fill="none"
              stroke="currentColor"
              strokeWidth="0.5"
              opacity="0.08"
            />
          </pattern>
        </defs>
        <g className="grid-drift">
          <rect x="-100" width="200%" height="100%" fill="url(#grid)" />
        </g>
      </svg>

      {/* The skyline, pinned to the bottom */}
      <svg
        className="absolute bottom-0 left-0 w-full"
        viewBox="0 0 1440 520"
        fill="none"
        preserveAspectRatio="xMidYMax meet"
        aria-hidden="true"
        style={{ minWidth: "1000px" }}
      >
        <g
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          fill="none"
        >
          {/* ground line */}
          <line
            className="draw-path"
            style={{ ["--dash" as string]: "1440", ["--delay" as string]: "0s" }}
            x1="0"
            y1="500"
            x2="1440"
            y2="500"
            opacity="0.6"
          />

          {/* --- Tall tower (left) --- */}
          <g className="float-slow" style={{ ["--delay" as string]: "0.2s" }}>
            <path
              className="draw-path"
              style={{ ["--dash" as string]: "900", ["--delay" as string]: "0.2s" }}
              d="M120 500 V180 H260 V500"
            />
            <line className="draw-path" style={{ ["--dash" as string]: "150", ["--delay" as string]: "1s" }} x1="120" y1="230" x2="260" y2="230" opacity="0.7" />
            <line className="draw-path" style={{ ["--dash" as string]: "150", ["--delay" as string]: "1.1s" }} x1="120" y1="290" x2="260" y2="290" opacity="0.7" />
            <line className="draw-path" style={{ ["--dash" as string]: "150", ["--delay" as string]: "1.2s" }} x1="120" y1="350" x2="260" y2="350" opacity="0.7" />
            <line className="draw-path" style={{ ["--dash" as string]: "150", ["--delay" as string]: "1.3s" }} x1="120" y1="410" x2="260" y2="410" opacity="0.7" />
            {/* antenna */}
            <line className="draw-path" style={{ ["--dash" as string]: "60", ["--delay" as string]: "1.5s" }} x1="190" y1="180" x2="190" y2="130" />
            <circle className="win-pulse" style={{ ["--delay" as string]: "2s" }} cx="190" cy="126" r="4" fill="currentColor" stroke="none" />
          </g>

          {/* --- Mid-rise with setback (center-left) --- */}
          <g className="float-slow" style={{ ["--delay" as string]: "0.9s" }}>
            <path
              className="draw-path"
              style={{ ["--dash" as string]: "1000", ["--delay" as string]: "0.4s" }}
              d="M300 500 V300 H360 V240 H460 V300 H520 V500"
            />
            {[0, 1, 2, 3].map((r) =>
              [0, 1, 2].map((c) => (
                <rect
                  key={`m-${r}-${c}`}
                  className="win-pulse"
                  style={{ ["--delay" as string]: `${1.4 + r * 0.15 + c * 0.1}s` }}
                  x={330 + c * 55}
                  y={330 + r * 34}
                  width="20"
                  height="20"
                  fill="currentColor"
                  stroke="none"
                  opacity="0.15"
                />
              )),
            )}
          </g>

          {/* --- The house (center) --- */}
          <g className="float-slow" style={{ ["--delay" as string]: "1.2s" }}>
            <path
              className="draw-path"
              style={{ ["--dash" as string]: "700", ["--delay" as string]: "0.9s" }}
              d="M600 500 V400 L680 340 L760 400 V500"
            />
            {/* roof line */}
            <path className="draw-path" style={{ ["--dash" as string]: "260", ["--delay" as string]: "1.4s" }} d="M600 400 L680 340 L760 400" />
            {/* door */}
            <path className="draw-path" style={{ ["--dash" as string]: "150", ["--delay" as string]: "1.8s" }} d="M660 500 V440 H700 V500" opacity="0.8" />
            {/* window */}
            <rect className="win-pulse" style={{ ["--delay" as string]: "2.2s" }} x="620" y="420" width="24" height="24" fill="currentColor" stroke="none" />
            <rect className="win-pulse" style={{ ["--delay" as string]: "2.4s" }} x="716" y="420" width="24" height="24" fill="currentColor" stroke="none" />
          </g>

          {/* --- Skyscraper (center-right) --- */}
          <g className="float-slow" style={{ ["--delay" as string]: "0.5s" }}>
            <path
              className="draw-path"
              style={{ ["--dash" as string]: "1100", ["--delay" as string]: "0.6s" }}
              d="M820 500 V120 H940 V500"
            />
            <path className="draw-path" style={{ ["--dash" as string]: "260", ["--delay" as string]: "1.6s" }} d="M820 120 L880 80 L940 120" />
            {[0, 1, 2, 3, 4, 5].map((r) =>
              [0, 1].map((c) => (
                <rect
                  key={`s-${r}-${c}`}
                  className="win-pulse"
                  style={{ ["--delay" as string]: `${1.6 + r * 0.12 + c * 0.2}s` }}
                  x={845 + c * 50}
                  y={160 + r * 52}
                  width="24"
                  height="30"
                  fill="currentColor"
                  stroke="none"
                  opacity="0.15"
                />
              )),
            )}
          </g>

          {/* --- Twin blocks (right) --- */}
          <g className="float-slow" style={{ ["--delay" as string]: "1.5s" }}>
            <path className="draw-path" style={{ ["--dash" as string]: "800", ["--delay" as string]: "0.8s" }} d="M1000 500 V260 H1080 V500" />
            <path className="draw-path" style={{ ["--dash" as string]: "900", ["--delay" as string]: "1s" }} d="M1100 500 V200 H1200 V500" />
            <line className="draw-path" style={{ ["--dash" as string]: "100", ["--delay" as string]: "1.8s" }} x1="1000" y1="320" x2="1080" y2="320" opacity="0.6" />
            <line className="draw-path" style={{ ["--dash" as string]: "100", ["--delay" as string]: "1.9s" }} x1="1000" y1="390" x2="1080" y2="390" opacity="0.6" />
            <line className="draw-path" style={{ ["--dash" as string]: "120", ["--delay" as string]: "2s" }} x1="1100" y1="290" x2="1200" y2="290" opacity="0.6" />
            <line className="draw-path" style={{ ["--dash" as string]: "120", ["--delay" as string]: "2.1s" }} x1="1100" y1="380" x2="1200" y2="380" opacity="0.6" />
          </g>

          {/* --- Small house (far right) --- */}
          <g className="float-slow" style={{ ["--delay" as string]: "1.8s" }}>
            <path className="draw-path" style={{ ["--dash" as string]: "500", ["--delay" as string]: "1.2s" }} d="M1260 500 V420 L1320 380 L1380 420 V500" />
            <path className="draw-path" style={{ ["--dash" as string]: "200", ["--delay" as string]: "1.7s" }} d="M1260 420 L1320 380 L1380 420" />
            <rect className="win-pulse" style={{ ["--delay" as string]: "2.3s" }} x="1300" y="440" width="40" height="60" fill="none" stroke="currentColor" opacity="0.7" />
          </g>
        </g>
      </svg>
    </div>
  )
}
